import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { addGroupUser, generateGroupUserOtp } from "../../../redux/group/groupSlice";
import { DailogBox } from "../../../components/Elements/DailogBox/DailogBox";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { Input, Button } from "antd";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import { postApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import { Check } from "lucide-react";

const GroupAddUser = ({ onOpen, onClose, onSave, clientId, groupName, groupOffsetId, validTill }) => {
    console.log(clientId, "clientid")
    const dispatch = useDispatch();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [otp, setOtp] = useState(""); // User will enter this
    const [loading, setLoading] = useState(false);
    const [otpSending, setOtpSending] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [errorMessage, setErrorMessage] = useState({
        name: "",
        email: "",
        password: "",
        otp: "",
    });

    const [popup, setPopup] = useState({
        visible: false,
        type: "success",
        title: "",
        message: "",
    });

    useEffect(() => {
        if (onOpen) {
            setName("");
            setEmail("");
            setPassword("");
            setOtp("");
            setOtpSent(false);
            setOtpVerified(false);
            setShowPassword(false);
            setErrorMessage({
                name: "",
                email: "",
                password: "",
                otp: "",
            });
        }
    }, [onOpen]);

    const getPayload = (currentOtp = 0) => {
        return {
            groupId: clientId,
            groupName,
            groupOffsetId,
            validTill: validTill || "2025-12-31",
            password,
            clientData: {
                basicInfo: { email, name },
                otp: Number(currentOtp),
                organization: {},
                billing: {}
            }
        };
    };

    /* OTP Logic Commented Out
    const handleSendOtp = async () => {
        if (!email) {
            setErrorMessage(prev => ({ ...prev, email: "Email is required to send OTP" }));
            return;
        }

        setOtpSending(true);
        try {
            const resultAction = await dispatch(generateGroupUserOtp({ email }));

            if (generateGroupUserOtp.fulfilled.match(resultAction)) {
                setOtpSent(true);
                setOtpVerified(false);
                setPopup({
                    visible: true,
                    type: "success",
                    title: "OTP Sent",
                    message: "OTP has been sent to your email successfully.",
                });
            } else {
                setPopup({
                    visible: true,
                    type: "error",
                    title: "Failed",
                    message: resultAction.payload || "Failed to send OTP. Please try again.",
                });
            }
        } catch (error) {
            setPopup({
                visible: true,
                type: "error",
                title: "Error",
                message: "Something went wrong while sending OTP.",
            });
        } finally {
            setOtpSending(false);
            setTimeout(() => setPopup(p => ({ ...p, visible: false })), 3000);
        }
    };

    const validateEmailField = () => {
        if (!email.trim()) {
            setErrorMessage(prev => ({ ...prev, email: "Email is required." }));
            return false;
        }

        if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)) {
            setPopup({
                visible: true,
                type: "warning",
                title: "Invalid Email",
                message: "Incorrect or invalid email format.",
            });
            return false;
        }

        return true;
    };

    const handleVerifyOtp = async () => {
        if (!validateEmailField()) return;
        if (!otpSent) {
            setPopup({
                visible: true,
                type: "warning",
                title: "OTP Not Sent",
                message: "Please send OTP before verifying it.",
            });
            setTimeout(() => setPopup(p => ({ ...p, visible: false })), 3000);
            return;
        }
        if (!otp) {
            setErrorMessage(prev => ({ ...prev, otp: "OTP is required" }));
            return;
        }

        try {
            const response = await postApi({
                apiUrl: API_URLS.CUSTOMER_VERIFY_OTP,
                data: {
                    OTP: otp,
                    email,
                    user_type: "buyer",
                },
            });

            if (response.success) {
                setOtpVerified(true);
                setPopup({
                    visible: true,
                    type: "success",
                    title: "OTP Verified",
                    message: "OTP verified successfully.",
                });
            } else {
                setOtpVerified(false);
                setPopup({
                    visible: true,
                    type: "error",
                    title: "OTP Verification Failed",
                    message: response.message || "OTP verification failed.",
                });
            }
        } catch (error) {
            setOtpVerified(false);
            setPopup({
                visible: true,
                type: "error",
                title: "OTP Verification Failed",
                message: error.message || "OTP verification failed.",
            });
        } finally {
            setTimeout(() => setPopup(p => ({ ...p, visible: false })), 3000);
        }
    };
    */

    const validate = () => {
        let isValid = true;
        const errors = { name: "", email: "", password: "", otp: "" };

        if (!name) { errors.name = "Name is required"; isValid = false; }
        if (!email) { errors.email = "Email is required"; isValid = false; }
        if (!password) { errors.password = "Password is required"; isValid = false; }

        setErrorMessage(errors);
        return isValid;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setLoading(true);

        try {
            const payload = getPayload(0); // Pass 0 as OTP since verification is bypassed
            const resultAction = await dispatch(addGroupUser({ data: payload, clientId }));

            if (addGroupUser.fulfilled.match(resultAction)) {
                setPopup({
                    visible: true,
                    type: "success",
                    title: "Success",
                    message: "User added successfully!",
                });

                if (onSave) onSave(); // trigger parent refresh

                // Automatically close everything after 2 seconds
                setTimeout(() => {
                    setPopup((p) => ({ ...p, visible: false }));
                    onClose();
                }, 2000);
            } else {
                setPopup({
                    visible: true,
                    type: "error",
                    title: "Error",
                    message: resultAction.payload || "Failed to add user. Please check your details.",
                });
            }

        } catch (error) {
            setPopup({
                visible: true,
                type: "error",
                title: "Error",
                message: error.message || "Something went wrong.",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setName("");
        setEmail("");
        setPassword("");
        setOtp("");
        setOtpSent(false);
        setOtpVerified(false);
        setShowPassword(false);
        setErrorMessage({ name: "", email: "", password: "", otp: "" });
        onClose();
    };

    const closePopup = () => {
        setPopup((p) => ({ ...p, visible: false }));
    };

    return (
        <>
            <DailogBox open={onOpen} onCancel={onClose} width="90%" maxWidth="800px">
                <div>
                    <h3 className="text-xl font-bold text-[#06513D]">Add New Group User</h3>
                </div>

                <div className="rounded-xl border mt-8 px-12 pb-8">
                    <div className="mt-3 mb-6">
                        <hr />
                    </div>

                    <div className="flex flex-col gap-6">
                        <div>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Name"
                                className={`h-14 rounded-lg ${errorMessage.name ? 'border-red-500' : ''}`}
                            />
                            {errorMessage.name && <div className="text-red-500 text-sm mt-1">{errorMessage.name}</div>}
                        </div>

                        <div>
                            <Input
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setErrorMessage(prev => ({ ...prev, email: "" }));
                                }}
                                placeholder="Email"
                                className={`h-14 rounded-lg ${errorMessage.email ? 'border-red-500' : ''}`}
                            />
                            {errorMessage.email && <div className="text-red-500 text-sm mt-1">{errorMessage.email}</div>}
                        </div>

                        <div>
                            <Input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setErrorMessage(prev => ({ ...prev, password: "" }));
                                }}
                                placeholder="Password"
                                className={`h-14 rounded-lg ${errorMessage.password ? 'border-red-500' : ''}`}
                            />
                            {errorMessage.password && <div className="text-red-500 text-sm mt-1">{errorMessage.password}</div>}
                            <label className="inline-flex items-center text-sm mt-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    className="mr-2"
                                    checked={showPassword}
                                    onChange={() => setShowPassword(!showPassword)}
                                    style={{ accentColor: "#06513D" }}
                                />
                                Show Password
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-center gap-6 mt-10">
                        <CustomButton
                            buttonType="button"
                            variant="cancel"
                            buttonText="Cancel"
                            onClick={handleCancel}
                            className="text-base font-normal h-[50px] w-[160px]"
                            disabled={loading}
                        />

                        <CustomButton
                            buttonType="button"
                            variant="ok"
                            buttonText={loading ? "Saving..." : "Save"}
                            onClick={handleSave}
                            className="text-base font-normal h-[50px] w-[160px]"
                            disabled={loading}
                        />
                    </div>
                </div>
            </DailogBox>

            {popup.visible && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
                    onClick={closePopup}
                >
                    <div onClick={(e) => e.stopPropagation()}>
                        <PopupCard
                            type={popup.type}
                            title={popup.title}
                            message={popup.message}
                            autoClose
                            autoCloseMs={3000}
                            onClose={closePopup}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default GroupAddUser;
